import { INavigatorRepository } from "../../adapters/driven/repositories/navigatorRepository";

// APIProvider.GET swallows HTTP errors and returns the response body as data,
// so we detect error responses (e.g. 404) by inspecting the payload shape.
function isErrorResponse(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  return "detail" in (data as Record<string, unknown>);
}

export const NavigatorServiceImplFactory = ({
  navigatorRepository,
}: {
  navigatorRepository: INavigatorRepository;
}) => {
  // Helper to clear transcript localStorage for a given clinic + patient + visitNoteId
  const clearTranscriptLocalStorage = (
    clinicId: number,
    patientId: number,
    visitNoteId: number
  ) => {
    try {
      const storageKey = `transcript_segments_${clinicId}_${patientId}_${visitNoteId}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(
        "[IPV Service] Failed to clear transcript localStorage:",
        error
      );
    }
  };

  const linkPendingCustomTodoToNote = async ({
    clinicId,
    patientId,
    noteId,
  }: {
    clinicId: number;
    patientId: number;
    noteId: number;
  }): Promise<void> => {
    const {
      CUSTOM_TODO_LINK_RETRY_DELAYS_MS,
      clearPendingCustomTodoLink,
      getPendingCustomTodoLinkFor,
      waitForCustomTodoLinkRetry,
    } = await import("../../../util/navigatorCustomTodoHandoff");
    const pending = getPendingCustomTodoLinkFor({ patientId, clinicId });

    if (!pending) return;

    let lastError: unknown;
    for (
      let attempt = 0;
      attempt < CUSTOM_TODO_LINK_RETRY_DELAYS_MS.length;
      attempt += 1
    ) {
      const delay = CUSTOM_TODO_LINK_RETRY_DELAYS_MS[attempt];
      if (delay > 0) await waitForCustomTodoLinkRetry(delay);

      try {
        const response = await navigatorRepository.updateCustomTodoAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          todo_id: pending.customTodoId,
          ipv_note_id: noteId,
        });
        if (!isCustomTodoPayload(response)) {
          const message = extractErrorMessage(response);
          lastError = message;
          throw new Error(message);
        }
        clearPendingCustomTodoLink({
          customTodoId: pending.customTodoId,
          patientId: pending.patientId,
          clinicId: pending.clinicId,
        });
        return;
      } catch (err) {
        lastError = err;
      }

      console.warn("[Navigator] Custom todo link attempt failed:", {
        attempt: attempt + 1,
        customTodoId: pending.customTodoId,
        noteId,
        patientId,
        clinicId,
        error: lastError,
      });
    }

    console.warn(
      "[Navigator] Custom todo link failed after retries; keeping pending handoff:",
      {
        customTodoId: pending.customTodoId,
        noteId,
        patientId,
        clinicId,
        error: lastError,
      }
    );
  };

  const createInitialVisit: ICreateInitialVisit = async ({
    patientId,
    clinicId,
    visitData,
  }) => {
    const payload = {
      clinic_id: clinicId,
      patient_id: patientId,
      type: visitData.type,
      shortlisted: visitData.shortlisted,
      notes: visitData.notes,
      file_link: visitData.file_link,
      total_time: visitData.total_time || 0,
      context: visitData.context || {},
      doc_type: visitData.doc_type || "np_visit", // NEW: Default to np_visit for backward compatibility
    };

    const response = await navigatorRepository.createInitialVisitAPI(payload);

    // Clear transcript localStorage ONLY if this is a final submission (type === 1, not draft)
    // and ONLY after successful save
    if (visitData.type === 1 && response.data?.ipv_note_id) {
      clearTranscriptLocalStorage(
        clinicId,
        patientId,
        response.data.ipv_note_id
      );
    }

    // Side effect — link a freshly created note back to the Navigator custom
    // todo if the user navigated here from "Start Visit" on a custom-todo row.
    // Same intent as the side effect in VisitNotesController.createVisitNote;
    // we do it here too so the legacy IPV creation path stays in sync.
    if (payload.doc_type === "custom_visit" && response.data?.ipv_note_id) {
      try {
        await linkPendingCustomTodoToNote({
          patientId,
          clinicId,
          noteId: response.data.ipv_note_id,
        });
      } catch (err) {
        console.warn(
          "[Navigator] Failed to link custom todo to new IPV note:",
          err
        );
      }
    }

    return {
      success: true,
      data: response.data,
    };
  };

  const getIPVNotesByStatus: IGetIPVNotesByStatus = async ({
    clinicId,
    status,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesByStatusAPI({
        clinic_id: clinicId,
        status,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesByStatusv3: IGetIPVNotesByStatusv3 = async ({
    clinicId,
    status,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesByStatusAPIv3({
        clinic_id: clinicId,
        status,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const putIPVNote: IPutIPVNote = async ({
    patientId,
    clinicId,
    ipvNoteId,
    visitData,
  }) => {
    try {
      const payload = {
        clinic_id: clinicId,
        patient_id: patientId,
        ipv_note_id: ipvNoteId,
        type: visitData.type,
        shortlisted: visitData.shortlisted,
        notes: visitData.notes,
        file_link: visitData.file_link,
        total_time: visitData.total_time,
        context: visitData.context || {},
        doc_type: visitData.doc_type || "np_visit", // NEW: Default to np_visit for backward compatibility
      };

      const response = await navigatorRepository.putIPVNoteAPI(payload);

      // Clear transcript localStorage ONLY if this is a final submission (type === 1, not draft)
      // and ONLY after successful save
      if (visitData.type === 1) {
        clearTranscriptLocalStorage(clinicId, patientId, ipvNoteId);
      }

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesByPatientID: IGetIPVNotesByPatientID = async ({
    patientId,
    clinicId,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesByPatientIDAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      // Backend returns null if no note exists, or IInitialPatientVisit2 object
      return {
        success: true,
        data: response || undefined, // Convert null to undefined for consistent typing
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getIPVNotesForMonthlyReport: IGetIPVNotesForMonthlyReport = async ({
    patientId,
    clinicId,
    month,
  }) => {
    try {
      const response = await navigatorRepository.getIPVNotesForMonthlyReportAPI(
        {
          clinic_id: clinicId,
          patient_id: patientId,
          month,
        }
      );
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getNpSupervisingPhysician: IGetNpSupervisingPhysician = async ({
    npUserId,
  }) => {
    try {
      const response = await navigatorRepository.getNpSupervisingPhysicianAPI({
        np_user_id: npUserId,
      });
      // Unwrap the top-level `data` envelope so consumers get the flat payload.
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const deleteIPVNote: IDeleteIPVNote = async ({
    patientId,
    clinicId,
    ipvNoteId,
  }) => {
    try {
      const response = await navigatorRepository.deleteIPVNoteAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        ipv_note_id: ipvNoteId,
      });
      if (isErrorResponse(response)) {
        throw new Error(extractErrorMessage(response));
      }

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const deleteIPVDraftAndSession: IDeleteIPVDraftAndSession = async ({
    patientId,
    clinicId,
    ipvNoteId,
    sessionId,
  }) => {
    try {
      const response = await navigatorRepository.deleteIPVDraftAndSessionAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        ipv_note_id: ipvNoteId,
        session_id: sessionId,
      });
      if (
        isErrorResponse(response) ||
        (ipvNoteId && response?.ipv_draft_deleted !== true)
      ) {
        throw new Error(extractErrorMessage(response));
      }

      // Clear transcript localStorage after successful deletion
      if (ipvNoteId) {
        clearTranscriptLocalStorage(clinicId, patientId, ipvNoteId);
      }

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientsForNavigator: IGetPatientsForNavigator = async ({
    clinicId,
    bypassIpvGate,
  }) => {
    try {
      const response = await navigatorRepository.getPatientsForNavigatorAPI({
        clinic_id: clinicId,
        ...(bypassIpvGate ? { bypass_ipv_gate: true } : {}),
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getNavigatorPatientById: IGetNavigatorPatientById = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await navigatorRepository.getNavigatorPatientByIdAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      if (isErrorResponse(response)) {
        return {
          success: false,
          error: {
            message: (response as any)?.detail?.message ?? "Patient not found",
          },
        };
      }
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getCustomTodoVisits: IGetCustomTodoVisits = async ({
    clinicId,
    state,
    includeFuture,
  }) => {
    try {
      const response = await navigatorRepository.getCustomTodoVisitsAPI({
        clinic_id: clinicId,
        state,
        include_future: includeFuture,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updatePatientExtraInfo: IUpdatePatientExtraInfo = async ({
    patientId,
    clinicId,
    extraInfo,
    patientInfo,
  }) => {
    try {
      const response = await navigatorRepository.updatePatientExtraInfoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        ...(extraInfo !== undefined && { extra_info: extraInfo }),
        ...(patientInfo !== undefined && { patient_info: patientInfo }),
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const manageSavedPatientAddress: IManageSavedPatientAddress = async ({
    patientId,
    clinicId,
    action,
    addressId,
    formattedAddress,
    label,
    makeDefault,
  }) => {
    try {
      const response = await navigatorRepository.manageSavedPatientAddressAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        action,
        address_id: addressId,
        formatted_address: formattedAddress,
        label,
        make_default: makeDefault,
      });
      return {
        success: true,
        data: response.data.saved_addresses,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientVisitNotes: IGetPatientVisitNotes = async ({
    clinic_id,
    patient_id,
  }) => {
    try {
      const response = await navigatorRepository.getPatientVisitNotesAPI({
        clinic_id: clinic_id,
        patient_id: patient_id,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: error instanceof Error ? error.message : String(error),
        },
      };
    }
  };

  const storePreChartNote: IStorePreChartNote = async ({
    clinicId,
    patientId,
    apptNote,
  }) => {
    try {
      const response = await navigatorRepository.storePreChartNoteAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        apptNote: apptNote,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getAtRiskReviewLockStatus = async ({
    clinicId,
  }: {
    clinicId: number;
  }): Promise<{
    success: boolean;
    data?: IAtRiskReviewLockStatus;
    error?: { message: string };
  }> => {
    try {
      const response = await navigatorRepository.getAtRiskReviewLockStatusAPI({
        clinic_id: clinicId,
      });
      return {
        success: true,
        data: response as IAtRiskReviewLockStatus,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const postAtRiskReviewOverrideRequest = async ({
    clinicId,
    reasons,
  }: {
    clinicId: number;
    reasons: { patient_id: number; reason: string }[];
  }): Promise<{
    success: boolean;
    data?: { message: string; request_id: string };
    error?: { message: string };
  }> => {
    try {
      const response =
        await navigatorRepository.postAtRiskReviewOverrideRequestAPI({
          clinic_id: clinicId,
          reasons,
        });
      return {
        success: true,
        data: response as { message: string; request_id: string },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const patchAtRiskReviewOverrideRequest = async ({
    clinicId,
    reasons,
  }: {
    clinicId: number;
    reasons: { patient_id: number; reason: string }[];
  }): Promise<{
    success: boolean;
    error?: { message: string };
  }> => {
    try {
      await navigatorRepository.patchAtRiskReviewOverrideRequestAPI({
        clinic_id: clinicId,
        reasons,
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  // Distinguish a real CustomTodoResponse payload from an APIProvider-swallowed
  // error body. APIProvider returns `{ detail: ... }` (or similar) on 4xx/5xx
  // instead of throwing, so we discriminate on the presence of `id` + `state`.
  const isCustomTodoPayload = (data: unknown): data is ICustomTodo => {
    if (!data || typeof data !== "object") return false;
    const obj = data as Record<string, unknown>;
    return typeof obj.id === "number" && typeof obj.state === "string";
  };

  const isCustomTodoRecurrencePayload = (
    data: unknown
  ): data is ICustomTodoRecurrence => {
    if (!data || typeof data !== "object") return false;
    const obj = data as Record<string, unknown>;
    return (
      typeof obj.id === "number" &&
      Array.isArray(obj.reasons) &&
      typeof obj.cadence_days === "number"
    );
  };

  const extractErrorMessage = (data: unknown): string => {
    if (!data || typeof data !== "object") return "Request failed";
    const obj = data as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    if (
      obj.detail &&
      typeof obj.detail === "object" &&
      typeof (obj.detail as { message?: unknown }).message === "string"
    ) {
      return (obj.detail as { message: string }).message;
    }
    if (typeof obj.message === "string") return obj.message;
    return "Request failed";
  };

  // BE handoff: 409 returns "Patient already has an open custom todo." —
  // we surface that as `conflict: true` so the UI can flip to disabled state.
  const looksLikeOpenTodoConflict = (message: string): boolean =>
    /already has an open custom todo/i.test(message) ||
    /already has an open/i.test(message);

  const createCustomTodo: ICreateCustomTodo = async ({
    clinicId,
    patientId,
    reasons,
    admissionId,
    dueDate,
    source,
  }) => {
    try {
      const response = await navigatorRepository.createCustomTodoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        reasons,
        admission_id: admissionId ?? null,
        due_date: dueDate ?? null,
        source,
      });

      if (isCustomTodoPayload(response)) {
        return { success: true, data: response };
      }

      const message = extractErrorMessage(response);
      return {
        success: false,
        conflict: looksLikeOpenTodoConflict(message),
        error: { message },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const createCustomTodoRecurrence: ICreateCustomTodoRecurrence = async ({
    clinicId,
    patientId,
    reasons,
    cadenceDays,
    startDate,
    endDate,
    admissionId,
  }) => {
    try {
      const response = await navigatorRepository.createCustomTodoRecurrenceAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        reasons,
        cadence_days: cadenceDays,
        start_date: startDate,
        end_date: endDate ?? null,
        admission_id: admissionId ?? null,
      });

      if (isCustomTodoRecurrencePayload(response)) {
        return { success: true, data: response };
      }

      return {
        success: false,
        error: { message: extractErrorMessage(response) },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getCustomTodoRecurrences: IGetCustomTodoRecurrences = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await navigatorRepository.getCustomTodoRecurrencesAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });

      if (response && Array.isArray((response as { rows?: unknown }).rows)) {
        return { success: true, data: response };
      }

      return {
        success: false,
        error: { message: extractErrorMessage(response) },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getClinicCustomTodoRecurrences: IGetClinicCustomTodoRecurrences =
    async ({ clinicId, includeInactive }) => {
      try {
        const response =
          await navigatorRepository.getClinicCustomTodoRecurrencesAPI({
            clinic_id: clinicId,
            include_inactive: includeInactive,
          });

        if (response && Array.isArray((response as { rows?: unknown }).rows)) {
          return { success: true, data: response };
        }

        return {
          success: false,
          error: { message: extractErrorMessage(response) },
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const deleteCustomTodoRecurrence: IDeleteCustomTodoRecurrence = async ({
    clinicId,
    patientId,
    scheduleId,
  }) => {
    try {
      const response = await navigatorRepository.deleteCustomTodoRecurrenceAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        schedule_id: scheduleId,
      });

      if (
        response &&
        typeof (response as { id?: unknown }).id === "number" &&
        (response as { deleted?: unknown }).deleted === true
      ) {
        return { success: true, data: response };
      }

      return {
        success: false,
        error: { message: extractErrorMessage(response) },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updateCustomTodoRecurrence: IUpdateCustomTodoRecurrence = async (
    params
  ) => {
    const { clinicId, patientId, scheduleId, reasons, cadenceDays, endDate } =
      params;
    try {
      const payload: Parameters<IUpdateCustomTodoRecurrenceAPI>[0] = {
        clinic_id: clinicId,
        patient_id: patientId,
        schedule_id: scheduleId,
      };
      if (reasons !== undefined) {
        payload.reasons = reasons;
      }
      if (cadenceDays !== undefined) {
        payload.cadence_days = cadenceDays;
      }
      if (Object.prototype.hasOwnProperty.call(params, "endDate")) {
        payload.end_date = endDate ?? null;
      }

      const response = await navigatorRepository.updateCustomTodoRecurrenceAPI(
        payload
      );

      if (isCustomTodoRecurrencePayload(response)) {
        return { success: true, data: response };
      }

      return {
        success: false,
        error: { message: extractErrorMessage(response) },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updateCustomTodo: IUpdateCustomTodo = async ({
    clinicId,
    patientId,
    todoId,
    reasons,
    ipvNoteId,
  }) => {
    try {
      // Build the payload so we forward `null` for unlink but omit when unchanged.
      const payload: Parameters<IUpdateCustomTodoAPI>[0] = {
        clinic_id: clinicId,
        patient_id: patientId,
        todo_id: todoId,
      };
      if (reasons !== undefined) payload.reasons = reasons;
      if (ipvNoteId !== undefined) payload.ipv_note_id = ipvNoteId;

      const response = await navigatorRepository.updateCustomTodoAPI(payload);

      if (isCustomTodoPayload(response)) {
        return { success: true, data: response };
      }

      return {
        success: false,
        error: { message: extractErrorMessage(response) },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const deleteCustomTodo: IDeleteCustomTodo = async ({
    clinicId,
    patientId,
    todoId,
  }) => {
    try {
      const response = await navigatorRepository.deleteCustomTodoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        todo_id: todoId,
      });

      if (
        response &&
        typeof response === "object" &&
        (response as { deleted?: boolean }).deleted === true
      ) {
        return { success: true, data: response };
      }

      return {
        success: false,
        error: { message: extractErrorMessage(response) },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const patchNavigatorIpvIneligible: IPatchNavigatorIpvIneligible = async ({
    clinicId,
    patientId,
    admissionId,
    navigatorIpvIneligible,
    isExcluded,
    excludeReason,
  }) => {
    try {
      await navigatorRepository.patchNavigatorIpvIneligibleAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        admission_id: admissionId,
        ...(navigatorIpvIneligible !== undefined && {
          navigator_ipv_ineligible: navigatorIpvIneligible,
        }),
        ...(isExcluded !== undefined && { is_excluded: isExcluded }),
        ...(excludeReason !== undefined && { exclude_reason: excludeReason }),
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    createInitialVisit,
    getIPVNotesByStatus,
    getIPVNotesByStatusv3,
    putIPVNote,
    getIPVNotesByPatientID,
    getIPVNotesForMonthlyReport,
    getNpSupervisingPhysician,
    deleteIPVNote,
    deleteIPVDraftAndSession,
    getPatientsForNavigator,
    getNavigatorPatientById,
    getCustomTodoVisits,
    updatePatientExtraInfo,
    manageSavedPatientAddress,
    getPatientVisitNotes,
    storePreChartNote,
    getAtRiskReviewLockStatus,
    postAtRiskReviewOverrideRequest,
    patchAtRiskReviewOverrideRequest,
    patchNavigatorIpvIneligible,
    createCustomTodo,
    getCustomTodoRecurrences,
    getClinicCustomTodoRecurrences,
    createCustomTodoRecurrence,
    updateCustomTodoRecurrence,
    deleteCustomTodoRecurrence,
    updateCustomTodo,
    deleteCustomTodo,
  };
};
